import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import {
  FiChevronRight,
  FiRefreshCw,
  FiPlus,
  FiEdit2,
  FiEye,
  FiTrash2,
  FiGrid,
  FiMenu,
} from "react-icons/fi";
import { useFetchVendorsQuery } from "../../../features/admin/vendor/vendorApi";
import {
  useFetchAllMutation,
  useDeleteAppUserMenuMutation,
} from "../../../features/admin/appUserMenu/appUserMenuApi";
import {
  useFetchAllAppUserDashboardMutation,
  useDeleteAppUserDashboardMutation,
} from "../../../features/admin/appUserDashboard/appUserDashboardApi";
import { normalizeIconUrl } from "../../../utils/appItemIcon";
import AppItemsEditor from "./AppItemsEditor";
import {
  Page,
  PageHeader,
  Button,
  IconButton,
  SearchInput,
  Table,
  THead,
  Th,
  Td,
  EmptyRow,
  Badge,
  StatusDot,
  Modal,
} from "../../../components/ui";

const FALLBACK_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpath d='M21 15l-5-5L5 21'/%3E%3C/svg%3E";

const token = () => sessionStorage.getItem("access_token");

const ClientAppSetup = () => {
  const [refetch, setRefetch] = useState(0);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({}); // tenant_id -> bool
  const [editor, setEditor] = useState(null); // { kind, tenant, userType, recordId, existingItems }
  const [viewer, setViewer] = useState(null); // { kind, tenant_name, userType, items }

  const { data: vendorsData, isFetching: vendorsFetching } =
    useFetchVendorsQuery(token(), { refetchOnMountOrArgChange: true }) || {};

  const [fetchMenus, { data: menuData, isLoading: menuLoading }] = useFetchAllMutation();
  const [fetchDashboards, { data: dashData, isLoading: dashLoading }] =
    useFetchAllAppUserDashboardMutation();
  const [deleteMenu] = useDeleteAppUserMenuMutation();
  const [deleteDashboard] = useDeleteAppUserDashboardMutation();

  useEffect(() => {
    fetchMenus({ token: token() });
    fetchDashboards({ token: token() });
  }, [refetch]);

  const triggerRefetch = () => setRefetch((n) => n + 1);

  // ── group everything tenant-wise, each tenant → user_types → {menu, dashboard} ──
  const tenants = useMemo(() => {
    const vendors = vendorsData?.body || [];
    const menus = menuData?.body || [];
    const dashboards = dashData?.body || [];
    const map = new Map();

    const ensureTenant = (tenant_id, name) => {
      if (!tenant_id && tenant_id !== 0) return null;
      if (!map.has(tenant_id)) {
        map.set(tenant_id, { tenant_id, tenant_name: name || `Tenant ${tenant_id}`, userTypes: new Map() });
      }
      const t = map.get(tenant_id);
      if (name && (!t.tenant_name || t.tenant_name.startsWith("Tenant "))) t.tenant_name = name;
      return t;
    };
    const ensureUT = (t, ut) => {
      if (!t.userTypes.has(ut))
        t.userTypes.set(ut, { user_type: ut, menu: null, dashboard: null, user_type_id: null });
      return t.userTypes.get(ut);
    };

    vendors.forEach((v) => {
      if (!v.tenant_id && v.tenant_id !== 0) return;
      const t = ensureTenant(v.tenant_id, v.name);
      (v.user_types || []).forEach((ut) => ensureUT(t, ut));
    });
    menus.forEach((m) => {
      const t = ensureTenant(m.tenant_id, m.tenant_name);
      if (!t) return;
      const u = ensureUT(t, m.user_type);
      u.menu = m;
      if (m.user_type_id) u.user_type_id = m.user_type_id;
    });
    dashboards.forEach((d) => {
      const t = ensureTenant(d.tenant_id, d.tenant_name);
      if (!t) return;
      const u = ensureUT(t, d.user_type);
      u.dashboard = d;
      if (d.user_type_id) u.user_type_id = d.user_type_id;
    });

    return Array.from(map.values())
      .map((t) => {
        const userTypes = Array.from(t.userTypes.values()).sort((a, b) =>
          a.user_type.localeCompare(b.user_type),
        );
        const incomplete = userTypes.filter((u) => !u.menu || !u.dashboard).length;
        return {
          ...t,
          userTypes,
          menuCount: userTypes.filter((u) => u.menu).length,
          dashCount: userTypes.filter((u) => u.dashboard).length,
          incomplete,
          complete: userTypes.length > 0 && incomplete === 0,
        };
      })
      .sort((a, b) => (a.tenant_name || "").localeCompare(b.tenant_name || ""));
  }, [vendorsData, menuData, dashData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter(
      (t) =>
        (t.tenant_name || "").toLowerCase().includes(q) ||
        String(t.tenant_id).includes(q),
    );
  }, [tenants, search]);

  const loading = menuLoading || dashLoading || vendorsFetching;

  // ── actions ─────────────────────────────────────────────────────────────
  const openEditor = (kind, tenant, u) =>
    setEditor({
      kind,
      tenant: { tenant_id: tenant.tenant_id, tenant_name: tenant.tenant_name },
      userType: u.user_type,
      recordId: kind === "menu" ? u.menu?.id ?? null : u.dashboard?.id ?? null,
      existingItems: kind === "menu" ? u.menu?.app_menu || [] : u.dashboard?.app_dashboard || [],
    });

  const openViewer = (kind, tenant, u) => {
    const record = kind === "menu" ? u.menu : u.dashboard;
    setViewer({
      kind,
      tenant_name: tenant.tenant_name,
      userType: u.user_type,
      items: kind === "menu" ? record?.app_menu || [] : record?.app_dashboard || [],
      createdAt: record?.created_at || null,
      updatedAt: record?.updated_at || null,
    });
  };

  const confirmDelete = (kind, u) => {
    const record = kind === "menu" ? u.menu : u.dashboard;
    if (!record) return;
    toast(
      (t) => (
        <span>
          <h6>Delete this {kind === "menu" ? "menu" : "dashboard"} config?</h6>
          <div className="mt-3 d-flex">
            <button
              className="btn btn-outline-danger btn-sm ml-auto mr-1"
              onClick={() => {
                toast.dismiss(t.id);
                doDelete(kind, record.id);
              }}
            >
              Yes
            </button>
            <button
              className="btn btn-outline-primary btn-sm mr-auto ml-1"
              onClick={() => toast.dismiss(t.id)}
            >
              No
            </button>
          </div>
        </span>
      ),
      { id: "deleteAppConfig" },
    );
  };

  const doDelete = async (kind, id) => {
    try {
      const args = { token: token(), id };
      await (kind === "menu" ? deleteMenu(args) : deleteDashboard(args)).unwrap();
      toast.success("Deleted");
      triggerRefetch();
    } catch {
      toast.error("Could not delete");
    }
  };

  // ── config cell used in the expanded per-user-type table ────────────────
  const ConfigCell = ({ kind, tenant, u }) => {
    const record = kind === "menu" ? u.menu : u.dashboard;
    const count =
      kind === "menu" ? u.menu?.app_menu?.length || 0 : u.dashboard?.app_dashboard?.length || 0;

    if (!record) {
      return (
        <div className="flex items-center gap-2">
          <StatusDot tone="amber" />
          <Button
            size="xs"
            variant="dashed"
            icon={<FiPlus className="h-3.5 w-3.5" />}
            onClick={() => openEditor(kind, tenant, u)}
          >
            Add
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        <StatusDot tone="green" />
        <Badge tone="green">
          {count} item{count === 1 ? "" : "s"}
        </Badge>
        <Button
          size="xs"
          variant="outline"
          icon={<FiEye className="h-3.5 w-3.5" />}
          onClick={() => openViewer(kind, tenant, u)}
        >
          View
        </Button>
        <Button
          size="xs"
          variant="soft"
          icon={<FiEdit2 className="h-3.5 w-3.5" />}
          onClick={() => openEditor(kind, tenant, u)}
        >
          Edit
        </Button>
        <IconButton
          tone="red"
          icon={<FiTrash2 className="h-3.5 w-3.5" />}
          onClick={() => confirmDelete(kind, u)}
          className="border border-gray-200"
        />
      </div>
    );
  };

  const tenantStatus = (t) => {
    if (t.userTypes.length === 0) return <StatusDot tone="gray" label="No user types" />;
    if (t.complete) return <StatusDot tone="green" label="All set" />;
    return <StatusDot tone="amber" label={`${t.incomplete} incomplete`} />;
  };

  return (
    <Page>
      <PageHeader
        title="App Setup by Client"
        subtitle="Manage each client's app menu & dashboard per user type in one place."
        actions={
          <Button
            variant="outline"
            icon={<FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />}
            onClick={triggerRefetch}
          >
            Refresh
          </Button>
        }
      />

      <SearchInput
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search clients by name or ID…"
        className="mb-3"
      />

      <Table>
        <THead>
          <tr>
            <th className="w-10 px-2 py-2.5" />
            <Th className="w-12">#</Th>
            <Th>Client</Th>
            <Th>User types</Th>
            <Th>Menus</Th>
            <Th>Dashboards</Th>
            <Th>Status</Th>
          </tr>
        </THead>
        <tbody>
          {loading && filtered.length === 0 && <EmptyRow colSpan={7}>Loading clients…</EmptyRow>}
          {!loading && filtered.length === 0 && <EmptyRow colSpan={7}>No clients found.</EmptyRow>}

          {filtered.map((tenant, ti) => {
            const isOpen = !!expanded[tenant.tenant_id];
            return (
              <Fragment key={tenant.tenant_id}>
                <tr
                  onClick={() =>
                    setExpanded((prev) => ({ ...prev, [tenant.tenant_id]: !prev[tenant.tenant_id] }))
                  }
                  className={`border-t border-gray-100 cursor-pointer hover:bg-gray-50/70 ${
                    isOpen ? "bg-gray-50/70" : ""
                  }`}
                >
                  <td className="px-2 py-2.5 text-center align-middle">
                    <FiChevronRight
                      className={`inline h-4 w-4 text-gray-400 transition-transform ${
                        isOpen ? "rotate-90" : ""
                      }`}
                    />
                  </td>
                   <Td className="text-gray-400 text-xs">{ti + 1}</Td>
                  <Td className="font-medium text-gray-900">
                    {tenant.tenant_name}
                    <span className="ml-1.5 text-xs font-normal text-gray-400">
                      (ID: {tenant.tenant_id})
                    </span>
                  </Td>
                  <Td className="text-gray-600">{tenant.userTypes.length}</Td>
                  <Td className="text-gray-600">
                    <span className="inline-flex items-center gap-1">
                      <FiMenu className="h-3.5 w-3.5 text-gray-400" />
                      {tenant.menuCount}/{tenant.userTypes.length}
                    </span>
                  </Td>
                  <Td className="text-gray-600">
                    <span className="inline-flex items-center gap-1">
                      <FiGrid className="h-3.5 w-3.5 text-gray-400" />
                      {tenant.dashCount}/{tenant.userTypes.length}
                    </span>
                  </Td>
                  <Td>{tenantStatus(tenant)}</Td>
                </tr>

                {isOpen && (
                  <tr className="border-t border-gray-100 bg-gray-50/40">
                    <td colSpan={6} className="px-4 md:px-6 py-4">
                      {tenant.userTypes.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-4">
                          No user types set up for this client.
                        </p>
                      ) : (
                        <Table>
                          <THead>
                            <tr>
                              <Th className="w-52">User type</Th>
                              <Th>Menu</Th>
                              <Th>Dashboard</Th>
                            </tr>
                          </THead>
                          <tbody>
                            {tenant.userTypes.map((u) => {
                              const rowIncomplete = !u.menu || !u.dashboard;
                              return (
                                <tr key={u.user_type} className="border-t border-gray-100">
                                  <Td>
                                    <span className="inline-flex items-center gap-2">
                                      {rowIncomplete && <StatusDot tone="amber" />}
                                      <Badge tone="indigo" className="capitalize">
                                        {u.user_type}
                                      </Badge>
                                    </span>
                                  </Td>
                                  <Td>
                                    <ConfigCell kind="menu" tenant={tenant} u={u} />
                                  </Td>
                                  <Td>
                                    <ConfigCell kind="dashboard" tenant={tenant} u={u} />
                                  </Td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </Table>

      {/* editor drawer */}
      {editor && (
        <AppItemsEditor
          kind={editor.kind}
          tenant={editor.tenant}
          userType={editor.userType}
          recordId={editor.recordId}
          existingItems={editor.existingItems}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            triggerRefetch();
          }}
        />
      )}

      {/* view modal */}
      {viewer && (
        <Modal
          size="sm"
          onClose={() => setViewer(null)}
          title={`${viewer.kind === "menu" ? "Menu" : "Dashboard"} items`}
          subtitle={
            <>
              {viewer.tenant_name} · <span className="capitalize">{viewer.userType}</span>
            </>
          }
          headerRight={
            (viewer.createdAt || viewer.updatedAt) && (
              <div className="shrink-0 text-right text-[11px] text-gray-400 space-y-0.5">
                {viewer.createdAt && (
                  <div>
                    Created:{" "}
                    <span className="text-gray-500">{new Date(viewer.createdAt).toLocaleString()}</span>
                  </div>
                )}
                {viewer.updatedAt && (
                  <div>
                    Updated:{" "}
                    <span className="text-gray-500">{new Date(viewer.updatedAt).toLocaleString()}</span>
                  </div>
                )}
              </div>
            )
          }
        >
          {viewer.items.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No items.</p>
          ) : (
            <ol className="space-y-2">
              {viewer.items.map((item, idx) => (
                <li key={`${item.id}-${idx}`} className="flex items-center gap-3">
                  <span className="w-6 text-xs font-semibold text-gray-400 text-center">{idx + 1}</span>
                  <img
                    src={normalizeIconUrl(item.icon) || FALLBACK_ICON}
                    alt={item.name}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = FALLBACK_ICON;
                    }}
                    className="h-7 w-7 object-contain shrink-0"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{item.name}</div>
                    <div className="text-xs text-gray-400 truncate">{item.action}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Modal>
      )}
    </Page>
  );
};

export default ClientAppSetup;
